import React, { useState, useEffect } from "react"
import styled from "styled-components"
import { em, rem } from "polished"
import { useCookies } from "react-cookie"

const COOKIE_NAME = `native-app-closed`

const NativeApp = ({
  translations,
  name,
  iosId,
  androidId,
  thumbnail,
  CloseSvg,
}) => {
  const [openUrl, setOpenUrl] = useState(``)
  const [cookies, setCookie] = useCookies([COOKIE_NAME])

  const closeClick = () => {
    setCookie(COOKIE_NAME, true, { path: `/`, maxAge: 3600 * 24 * 30 })
    setOpenUrl(``)
  }

  useEffect(() => {
    if (
      typeof window === `undefined` ||
      cookies[COOKIE_NAME] ||
      (!iosId && !androidId)
    )
      return

    const isIos = /(iPhone|iPad|iPod)/.test(window.navigator.userAgent)
    const isAndroid = /(Android)/.test(window.navigator.userAgent)

    if (!isIos && !isAndroid) return

    if (isIos) {
      if (iosId) setOpenUrl(`https://apps.apple.com/app/${iosId}`)
    } else if (androidId)
      setOpenUrl(`https://play.google.com/store/apps/details?id=${androidId}`)
  }, [])

  if (!openUrl) return null

  return (
    <Container>
      <Inner>
        <Close title={translations.global.close} onClick={closeClick}>
          {CloseSvg ? <CloseSvg aria-label={translations.global.close} /> : `x`}
        </Close>

        <Thumbnail>
          <img src={thumbnail} alt={name} />
        </Thumbnail>

        <Info>
          <h2>{name}</h2>

          <p>{translations.global.openInApp}</p>
        </Info>

        <Open>
          <a href={openUrl}>{translations.global.open}</a>
        </Open>
      </Inner>
    </Container>
  )
}

export default NativeApp

const Container = styled.section`
  width: 100%;
  padding-top: ${em(14)};
  padding-bottom: ${em(14)};
  position: fixed;
  z-index: ${({ theme }) => theme.zindex.nativeApp};
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  background-color: ${({ theme }) =>
    theme.components?.nativeApp?.backgroundColor || `white`};
  box-shadow: 0 ${rem(-4)} ${rem(4)} rgba(0, 0, 0, 0.25);

  @media ${({ theme }) => theme.mq.smallDown} {
    padding-top: ${em(10)};
    padding-bottom: ${em(10)};
  }
`

const Inner = styled.div`
  ${({ theme }) => theme.grid.container(480)}

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${em(20)};

  @media ${({ theme }) => theme.mq.smallDown} {
    width: 100%;
    max-width: auto;
    gap: ${em(10)};
  }
`

const Close = styled.button.attrs({ type: `button` })`
  width: ${em(40)};
  margin: 0 ${em(-10)};
  position: relative;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.components?.nativeApp?.closeColor || `#777`};

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.5;
  }

  svg {
    width: ${em(12)};
    height: 100%;
    display: block;
  }
`

const Thumbnail = styled.div`
  width: ${em(60)};
  padding: ${em(2)};
  flex-shrink: 0;
  border: 1px solid
    ${({ theme }) =>
      theme.components?.nativeApp?.thumbnailBorderColor || `#aaa`};
  border-radius: ${({ theme }) =>
    theme.components?.nativeApp?.thumbnailBorderRadius};
  overflow: hidden;

  @media ${({ theme }) => theme.mq.smallDown} {
    width: ${em(50)};
  }

  img {
    width: 100%;
    display: block;
  }
`

const Info = styled.div`
  h2 {
    ${({ theme }) => theme.components?.nativeApp?.titleFont || ``};

    font-size: ${em(15)};
  }

  p {
    font-size: ${em(12)};
    color: ${({ theme }) => theme.components?.nativeApp?.descriptionColor};
  }
`

const Open = styled.div`
  margin-left: auto;

  a {
    ${({ theme }) => theme.components?.nativeApp?.openFont || ``};

    padding: ${em(10)} ${em(20)};
    display: block;
    font-size: ${em(14)};
    color: ${({ theme }) => theme.components?.nativeApp?.openColor || `white`};
    background-color: ${({ theme }) =>
      theme.components?.nativeApp?.openBackgroundColor || `black`};
    border-radius: ${({ theme }) =>
      theme.components?.nativeApp?.openBorderRadius};

    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.5;
    }
  }
`
